import { ICalloutContentStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPSeparator, IPSpinner } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTarget } from "baseComponents/Utils/Target";
import AssetList from 'components/Asset/AssetList';
import LinkCreatorCustomList from 'components/Links/Creators/LinkCreatorCustomList';
import HrefLink from 'components/Links/HrefLink';
import Asset from 'models/asset/asset';
import ResourceLink from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAssets } from 'services/Api/assetService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnCode } from 'utils/sorting';

interface IAssetPickerProps {
  isOpen: boolean;
  target: IPTarget;
  onSelect: (items: Asset[]) => void;
  onDismiss: () => void;
  onSelectLinks?: (items: ResourceLink[]) => void;
  existingLinks: ResourceLink[];
  existingAssets?: number[];
  assetFilter?: (asset: Asset) => boolean;
  showLocationColumn?: boolean;
}

const AssetPicker = (props: IAssetPickerProps) => {
  const { t } = useTranslation(['translation', 'assets', 'classifications']);
  const appContext = useContext(AppContext);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [showList, setShowList] = useState<Asset | undefined>(undefined);

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const assets = await apiGetAssets(accessToken, appContext.globalDataCache);
      assets.sort((a, b) => sortOnCode(a.code, b.code));
      assets.forEach((asset) => {
        const inheritedClassifications = Asset.getInheritedClassifications(assets, asset);
        if (!asset.classifications) asset.classifications = [];
        asset.classifications.push(...inheritedClassifications);
      });
      if (props.assetFilter) {
        setAssets(assets.filter(props.assetFilter));
      } else {
        setAssets(assets);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: '10px 12px',
      minHeight: 300,
      height: '50vh',
      maxHeight: '80vh',
      minWidth: 300,
      width: '80vw',
    },
  };

  //
  // Main render
  //
  return (
    <IPCallout
      styles={calloutStyle}
      hidden={!props.isOpen}
      setInitialFocus
      gapSpace={0}
      target={props.target}
      onDismiss={() => props.onDismiss()}
      dismissOnTargetClick
    >
      <IPStack verticalFill>
        {!isLoading && assets.length === 0 && (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <HrefLink href="/assets">{t('assets:Picker.AssetLink')}</HrefLink>
          </IPStack>
        )}
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && assets.length > 0 && (
          <IPStack.Item grow>
            <AssetList
              assets={assets}
              onSelectAssets={(items) => setSelectedAssets(items)}
              isLoading={isLoading}
              showSearch={true}
              onListClick={(item: Asset) => setShowList(item)}
              existingAssets={props.existingAssets}
              showLocationColumn={props.showLocationColumn}
            />
          </IPStack.Item>
        )}
        <IPSeparator />
        <IPStack.Item>
          <IPPrimaryButton
            disabled={selectedAssets === undefined}
            onClick={() => {
              if (selectedAssets) {
                props.onSelect(selectedAssets);
              }
            }}
          >
            {t('translation:General.Button.Select')}
          </IPPrimaryButton>
        </IPStack.Item>
      </IPStack>
      {showList && (
        <LinkCreatorCustomList
          isOpen={showList !== undefined}
          list={appContext.globalDataCache.lists.get(showList.listId)}
          dismiss={() => setShowList(undefined)}
          addLinks={(items) => {
            if (props.onSelectLinks) {
              props.onSelectLinks(items);
            }
          }}
          existingLinks={props.existingLinks}
          listLoading={false}
        />
      )}
    </IPCallout>
  );
};

export default AssetPicker;
